import {
  GqlAgendaItem,
  GqlAgendaItemDecision,
  GqlAgendaItemKind,
  GqlMeeting,
  GqlMeetingKind,
  GqlMeetingParticipant,
  GqlWarrantProgramKind,
} from '../../../graphql/base-types.graphql';
import { AiAgendaApproval } from './agenda-items/AgendaApproval';
import { Trans } from '@lingui/react';
import { AiStandard } from './agenda-items/Standard';
import { VotingList } from '../../shareholders-meetings/components/VotingList';
import { AiMcProps, AiMeetingConcern } from './agenda-items/MeetingConcern';
import { isNil } from 'lodash-es';
import { Typography } from '@startuptools/ui/Typography';
import { authKindText } from '../../authorizations/Authorizations';
import { AiPriorMeetingsApproval } from './agenda-items/PriorMeetingsApproval';
import { AiNextMeeting } from './agenda-items/NextMeeting';
import { AiBmFunctionSelect } from './agenda-items/bm/FunctionSelect';
import { DisplayIdentity } from '@startuptools/business-ui/identity/Identity';
import { AiShmFunctionSelect } from './agenda-items/shm/FunctionSelect';

export const AgendaItemEditContent = ({
  meeting,
  agendaItem,
  meetingConcerns,
  agendaItems,
  kind,
}: {
  kind: GqlAgendaItemKind;
  agendaItem: Pick<GqlAgendaItem, 'id' | 'kind' | 'commentPrimaryLang' | 'commentSecondaryLang'> & {
    agendaItemDecisions: Pick<
      GqlAgendaItemDecision,
      'id' | 'order' | 'decisionPrimaryLang' | 'decisionSecondaryLang'
    >[];
  };
  meeting: Pick<GqlMeeting, 'id' | 'primaryLang' | 'secondaryLang' | 'companyId' | 'startTime' | 'kind'> & {
    meetingParticipants: (Pick<GqlMeetingParticipant, 'id' | 'present' | 'chairman' | 'secretary' | 'adjuster'> & {
      identity: DisplayIdentity;
      proxy?: DisplayIdentity | null;
    })[];
  };
  meetingConcerns: AiMcProps[];
  agendaItems: Pick<
    GqlAgendaItem,
    'id' | 'kind' | 'order' | 'subjectPrimaryLang' | 'subjectSecondaryLang' | 'firstGroup' | 'lastGroup' | 'mandatory'
  >[];
}) => {
  switch (kind) {
    case GqlAgendaItemKind.AgendaApproval: {
      return <AiAgendaApproval meeting={meeting} agendaItems={agendaItems} />;
    }
    case GqlAgendaItemKind.ArticlesOfAssoc: {
      return (
        <AiMeetingConcern
          details={mc => (
            <Typography>
              <Trans id="Articles of associations (Appendix {appendix})" values={{ appendix: mc.appendixNumber }} />
            </Typography>
          )}
          meetingConcerns={meetingConcerns.filter(mc => !isNil(mc.articlesOfAssoc))}
        />
      );
    }
    case GqlAgendaItemKind.AuthorizationGroup: {
      return (
        <AiMeetingConcern
          details={mc => (
            <Typography>
              <Trans
                id="Authorization for <auths></auths>"
                components={{
                  auths: mc.authorizationGroup!.authorizations.map((a, i) => (
                    <>
                      {authKindText[a.kind]}
                      {i < mc.authorizationGroup!.authorizations.length - 1 ? ', ' : ''}
                    </>
                  )),
                }}
              />
            </Typography>
          )}
          meetingConcerns={meetingConcerns.filter(mc => !isNil(mc.authorizationGroup))}
        />
      );
    }
    case GqlAgendaItemKind.CommentOnly: {
      return null;
    }
    case GqlAgendaItemKind.EmployeeOptionsProgram: {
      return (
        <AiMeetingConcern
          details={mc => (
            <Typography>
              <Trans
                id="Employee Options {name} with description: {description} (Appendix {appendix})"
                values={{
                  name: mc.warrantProgram!.name,
                  description: mc.warrantProgram!.description,
                  appendix: mc.appendixNumber,
                }}
              />
            </Typography>
          )}
          meetingConcerns={meetingConcerns.filter(
            mc => !isNil(mc.warrantProgram) && mc.warrantProgram.warrantKind === GqlWarrantProgramKind.Employee,
          )}
        />
      );
    }
    case GqlAgendaItemKind.FunctionSelect: {
      switch (meeting.kind) {
        case GqlMeetingKind.BoardMeeting: {
          return <AiBmFunctionSelect meeting={meeting} />;
        }
        case GqlMeetingKind.ShareholdersMeeting:
        case GqlMeetingKind.ExtraShareholdersMeeting: {
          return <AiShmFunctionSelect meeting={meeting} />;
        }
        default:
          throw new Error(`function select for ${meeting.kind} not implemented`);
      }
    }
    case GqlAgendaItemKind.MeetingEnd: {
      return null;
    }
    case GqlAgendaItemKind.MeetingStart: {
      return null;
    }
    case GqlAgendaItemKind.NextMeeting: {
      return <AiNextMeeting meeting={meeting} />;
    }
    case GqlAgendaItemKind.NiceConvertible: {
      return (
        <AiMeetingConcern
          details={mc => (
            <Typography>
              <Trans
                id="NICE {name} with description: {description} (Appendix {appendix})"
                values={{
                  name: mc.warrantProgram!.name,
                  description: mc.warrantProgram!.description,
                  appendix: mc.appendixNumber,
                }}
              />
            </Typography>
          )}
          meetingConcerns={meetingConcerns.filter(
            mc => !isNil(mc.warrantProgram) && mc.warrantProgram.warrantKind === GqlWarrantProgramKind.Nice,
          )}
        />
      );
    }
    case GqlAgendaItemKind.PriorMeetingApproval: {
      return <AiPriorMeetingsApproval meeting={meeting} />;
    }
    case GqlAgendaItemKind.ShareIssue: {
      return (
        <AiMeetingConcern
          details={mc => (
            <Typography>
              <Trans
                id="Share Issue with description: {description} (Appendix {appendix})"
                values={{ description: mc.shareIssue!.description, appendix: mc.appendixNumber }}
              />
            </Typography>
          )}
          meetingConcerns={meetingConcerns.filter(mc => !isNil(mc.shareIssue))}
        />
      );
    }
    case GqlAgendaItemKind.Standard: {
      return <AiStandard meeting={meeting} agendaItem={agendaItem} />;
    }
    case GqlAgendaItemKind.VotingList: {
      return <VotingList meeting={meeting} />;
    }
    case GqlAgendaItemKind.WarrantIssue: {
      return (
        <AiMeetingConcern
          details={mc => (
            <Typography>
              <Trans
                id="Warrant Program {name} with description: {description} (Appendix {appendix})"
                values={{
                  name: mc.warrantProgram!.name,
                  description: mc.warrantProgram!.description,
                  appendix: mc.appendixNumber,
                }}
              />
            </Typography>
          )}
          meetingConcerns={meetingConcerns.filter(
            mc => !isNil(mc.warrantProgram) && mc.warrantProgram.warrantKind === GqlWarrantProgramKind.Warrant,
          )}
        />
      );
    }
    case GqlAgendaItemKind.WiseConvertible: {
      return (
        <AiMeetingConcern
          details={mc => (
            <Typography>
              <Trans
                id="WISE {name} with description: {description} (Appendix {appendix})"
                values={{
                  name: mc.warrantProgram!.name,
                  description: mc.warrantProgram!.description,
                  appendix: mc.appendixNumber,
                }}
              />
            </Typography>
          )}
          meetingConcerns={meetingConcerns.filter(
            mc => !isNil(mc.warrantProgram) && mc.warrantProgram.warrantKind === GqlWarrantProgramKind.Wise,
          )}
        />
      );
    }
  }
};
